import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useLocation } from "wouter";
import { libelleZones } from "@shared/departements";
import { canDo } from "@shared/roles";
import { useResellers, useUpdateReseller } from "@/hooks/use-inventory";
import { useAuth } from "@/hooks/use-auth";
import { authFetch } from "@/lib/authFetch";
import { queryClient } from "@/lib/queryClient";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/status-badge";
import { CopyButton } from "@/components/CopyButton";
import { useToast } from "@/hooks/use-toast";
import { useRevendeurContrats, useCreateRevendeurContrat, useUpdateRevendeurContrat, useDeleteRevendeurContrat } from "@/hooks/use-reseller-contrats";
import { useRobotsReseller, useCreateRobotReseller, useUpdateRobotReseller, useDeleteRobotReseller } from "@/hooks/use-robots-clients";
import { useTicketsByReseller } from "@/hooks/use-tickets";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import AddressAutocomplete from "@/components/address-autocomplete";
import {
  Pencil, Loader2, Upload, Building2, ExternalLink,
  Navigation, Plus, Trash2, Star, MapPin, CheckCircle2, Circle, Smartphone,
  Bot, ZoomIn, ShieldCheck, X, AlertTriangle, TicketCheck, ShoppingCart,
  Target, Send, ScrollText, Users, UserCheck, UserX,
} from "lucide-react";
import type { Reseller, RevendeurContrat, RobotClient } from "@shared/schema";

// ─── Hook partagé permissions ─────────────────────────────────────────────────

export function useCanEditReseller(): boolean {
  const { profile } = useAuth();
  return canDo(profile?.accessRole) || profile?.role === "planification";
}

// ─── Constantes SitesSection ─────────────────────────────────────────────────

export const SITE_TYPE_LABELS: Record<string, string> = {
  point_de_vente: "Point de vente",
  depot: "Dépôt",
  sav: "SAV",
  siege: "Siège",
  autre: "Autre",
};

export const SITE_TYPE_COLORS: Record<string, string> = {
  point_de_vente: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
  depot: "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800",
  sav: "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
  siege: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800",
  autre: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700",
};

export const emptySiteForm = {
  nom: "", type_site: "point_de_vente", livraison_adresse: "", livraison_code_postal: "",
  livraison_ville: "", livraison_pays: "France", contact_reception_nom: "",
  contact_reception_email: "", contact_reception_tel: "", livraison_horaires: "",
  livraison_rdv_obligatoire: false, livraison_notes: "", est_site_par_defaut: false,
  latitude: null as number | null,
  longitude: null as number | null,
  geocode_score: null as number | null,
};

// ─── Helpers ResellerIdentitySection (dupliqués — à supprimer en Phase C) ───

const statusLabels: Record<string, string> = {
  revendeur: "Revendeur",
  revendeur_premium: "Revendeur Premium",
  sous_distributeur: "Sous-distributeur",
  apporteur_affaires: "Apporteur d'affaires",
  annule: "Annulé",
};

function hasRenewalSoon(contrats: RevendeurContrat[] | undefined): boolean {
  if (!contrats) return false;
  const now = new Date();
  const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  return contrats.some(
    (c) => c.statut === "en_cours" && c.dateFin && new Date(c.dateFin) <= in30 && new Date(c.dateFin) >= now,
  );
}

function ZonesCell({
  departementSiege,
  departementsCouverts,
}: {
  departementSiege?: string | null;
  departementsCouverts?: string[];
}) {
  const couverts = departementsCouverts ?? [];
  const label = libelleZones(departementSiege ?? null, couverts);
  const detail = [
    departementSiege ? `Siège : ${departementSiege}` : null,
    couverts.length ? `Couverts : ${couverts.join(", ")}` : "Aucun département couvert",
  ]
    .filter(Boolean)
    .join(" — ");
  if (label === "—") return <span className="text-slate-500">—</span>;
  return (
    <span
      title={detail}
      className="inline-flex items-center rounded-full bg-slate-700/60 px-2 py-0.5 text-xs text-slate-100 cursor-default"
    >
      {label}
    </span>
  );
}

// ─── ResellerQRSection ───────────────────────────────────────────────────────

export function ResellerQRSection() {
  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Smartphone className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm">Accès mobile — QR code</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Partagez ce QR code avec le revendeur pour qu'il installe le portail sur son téléphone.
          </p>
        </div>
        <div className="flex flex-col items-center gap-1.5 shrink-0">
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent("https://portail.jobotto.fr")}&size=120x120&bgcolor=FFFFFF&color=0B1220&qzone=2`}
            alt="QR Code Portail"
            className="w-24 h-24 rounded-lg border border-border"
          />
          <span className="text-[10px] text-muted-foreground">portail.jobotto.fr</span>
        </div>
      </div>
    </div>
  );
}

// ─── ResellerIdentitySection ─────────────────────────────────────────────────

export function ResellerIdentitySection({
  reseller,
  canEdit,
}: {
  reseller: Reseller;
  canEdit: boolean;
}) {
  const { data: contrats } = useRevendeurContrats(reseller.id);
  const renewal = hasRenewalSoon(contrats);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const updateMutation = useUpdateReseller();
  const { toast } = useToast();

  const { data: allResellers = [] } = useResellers();
  const parentReseller = (allResellers as any[]).find(
    (r: any) => r.id === (reseller as any).revendeurParentId,
  );
  const adherents = useMemo(
    () =>
      (allResellers as any[])
        .filter((r: any) => r.revendeurParentId === reseller.id && r.id !== reseller.id)
        .sort((a: any, b: any) => {
          if (a.actif !== b.actif) return a.actif ? -1 : 1;
          return ((a.nomCommercial || a.companyName) as string).localeCompare(
            b.nomCommercial || b.companyName,
          );
        }),
    [allResellers, reseller.id],
  );
  const isCentrale = adherents.length > 0;

  const handleToggleActif = async (checked: boolean) => {
    try {
      await updateMutation.mutateAsync({ id: reseller.id, actif: checked } as any);
      toast({ title: checked ? "Revendeur activé" : "Revendeur désactivé" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await authFetch(`/api/resellers/${reseller.id}/logo`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) throw new Error("Erreur upload logo");
      queryClient.invalidateQueries({ queryKey: ["/api/resellers"] });
      toast({ title: "Logo mis à jour" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <div className="relative group">
          {reseller.logoUrl ? (
            <img
              src={reseller.logoUrl}
              alt=""
              className="w-[100px] h-[100px] rounded-lg object-cover border shadow-sm"
            />
          ) : (
            <div className="w-[100px] h-[100px] rounded-lg bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center border shadow-sm">
              <Building2 className="w-10 h-10 text-purple-600 dark:text-purple-400" />
            </div>
          )}
          {canEdit && (
            <>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <Button
                variant="outline"
                size="sm"
                className="absolute bottom-1 right-1 invisible group-hover:visible"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                data-testid="button-upload-logo"
              >
                <Upload className="w-3 h-3" />
              </Button>
            </>
          )}
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-lg">
              {(reseller as any).nomCommercial || reseller.companyName}
            </h3>
            {canEdit ? (
              <div className="flex items-center gap-2" data-testid="toggle-actif-detail">
                <Switch
                  checked={reseller.actif !== false}
                  onCheckedChange={handleToggleActif}
                  disabled={updateMutation.isPending}
                />
                <span
                  className={`text-xs font-medium ${reseller.actif !== false ? "text-emerald-600" : "text-slate-500"}`}
                >
                  {reseller.actif !== false ? "Actif" : "Inactif"}
                </span>
              </div>
            ) : reseller.actif !== false ? (
              <Badge
                variant="outline"
                className="bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800"
                data-testid="badge-detail-actif"
              >
                Actif
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                data-testid="badge-detail-inactif"
              >
                Inactif
              </Badge>
            )}
            {renewal && (
              <Badge
                variant="outline"
                className="bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800"
                data-testid="badge-detail-renewal"
              >
                Renouvellement à venir
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge
              status={reseller.contractStatus}
              type="default"
              label={statusLabels[reseller.contractStatus]}
            />
            <span
              className="text-[10px] text-muted-foreground border border-dashed rounded px-1.5 py-0.5 border-slate-300 dark:border-slate-600"
              title="Ce champ ne pilote plus la résolution tarifaire — voir bloc Tarification partenaire"
            >
              legacy
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div>
          <span className="text-muted-foreground">SIREN :</span> {reseller.siren || "—"}
        </div>
        <div>
          <span className="text-muted-foreground">N° revendeur :</span>{" "}
          {reseller.resellerNumber || "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Code Pudu :</span>{" "}
          {(reseller as any).puduAgentCode ? (
            <span className="font-mono text-sm font-medium text-primary">
              {(reseller as any).puduAgentCode}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
        <div>
          <span className="text-muted-foreground">Code court :</span>{" "}
          {(reseller as any).codeCourt ? (
            <Badge
              variant="outline"
              className="bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 font-mono text-xs"
              data-testid="badge-code-court-detail"
            >
              {(reseller as any).codeCourt}
            </Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {(reseller as any).secteurActivite && (
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800 text-xs"
          >
            {(reseller as any).secteurActivite}
          </Badge>
        )}
        {parentReseller ? (
          <Badge
            variant="outline"
            className="bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-800 text-xs"
          >
            Groupe · {(parentReseller as any).nomCommercial || (parentReseller as any).companyName}
          </Badge>
        ) : isCentrale ? (
          <Badge
            variant="outline"
            className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800 text-xs"
          >
            Centrale
          </Badge>
        ) : null}
        {(reseller as any).anneesPartenariat > 0 && (
          <Badge
            variant="outline"
            className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800 text-xs"
          >
            {(reseller as any).anneesPartenariat} an
            {(reseller as any).anneesPartenariat > 1 ? "s" : ""} de partenariat
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">Adresse :</span>
        <span>{reseller.address || "—"}</span>
        {reseller.address && <CopyButton value={reseller.address} ariaLabel="Copier l'adresse" size="sm" />}
      </div>
      <div>
        <span className="text-muted-foreground">Contact :</span> {reseller.contactCivility}{" "}
        {reseller.contactFirstName} {reseller.contactLastName}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Tél :</span> {reseller.contactPhone || "—"}
          {reseller.contactPhone && (
            <CopyButton value={reseller.contactPhone} ariaLabel="Copier le numéro" size="sm" />
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Email :</span> {reseller.contactEmail || "—"}
          {reseller.contactEmail && (
            <CopyButton value={reseller.contactEmail} ariaLabel="Copier l'email" size="sm" />
          )}
        </div>
      </div>
      {(reseller as any).siteWeb && (
        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-muted-foreground">Site web :</span>
          <a
            href={
              (reseller as any).siteWeb.startsWith("http")
                ? (reseller as any).siteWeb
                : `https://${(reseller as any).siteWeb}`
            }
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline flex items-center gap-1"
          >
            {(reseller as any).siteWeb}
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">Zones :</span>
        <ZonesCell
          departementSiege={(reseller as any).departementSiege}
          departementsCouverts={(reseller as any).departementsCouverts}
        />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <span className="text-muted-foreground">Début :</span> {reseller.contractStartDate || "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Fin :</span> {reseller.contractEndDate || "—"}
        </div>
        <div>
          <span className="text-muted-foreground">Objectif :</span>{" "}
          {reseller.annualSalesTarget ?? "—"}
        </div>
      </div>
      {reseller.notes && (
        <div>
          <span className="text-muted-foreground">Notes :</span> {reseller.notes}
        </div>
      )}
    </div>
  );
}

// ─── AdherentsSection ──────────────────────────────────────────────────────────

export function AdherentsSection({
  reseller,
  onSelectReseller,
}: {
  reseller: Reseller;
  onSelectReseller?: (id: string) => void;
}) {
  const { data: allResellers = [] } = useResellers();
  const adherents = useMemo(
    () =>
      (allResellers as any[])
        .filter((r: any) => r.revendeurParentId === reseller.id && r.id !== reseller.id)
        .sort((a: any, b: any) => {
          if (a.actif !== b.actif) return a.actif ? -1 : 1;
          return ((a.nomCommercial || a.companyName) as string).localeCompare(
            b.nomCommercial || b.companyName,
          );
        }),
    [allResellers, reseller.id],
  );
  if (adherents.length === 0) return null;
  return (
    <div className="rounded-lg border bg-card px-3 py-2.5 space-y-2">
      <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
        Adhérents du groupe ({adherents.length})
      </span>
      <div className="space-y-0.5">
        {adherents.map((adherent: any) => {
          const nom = adherent.nomCommercial || adherent.companyName;
          const sub = adherent.codeCourt || adherent.siren || null;
          return (
            <button
              key={adherent.id}
              onClick={() => onSelectReseller?.(adherent.id)}
              className={`w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-accent transition-colors text-sm${!adherent.actif ? " opacity-50" : ""}`}
              data-testid={`button-adherent-${adherent.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium truncate">{nom}</span>
                {sub && (
                  <span className="text-xs text-muted-foreground shrink-0">{sub}</span>
                )}
                {!adherent.actif && (
                  <span className="text-[10px] text-muted-foreground shrink-0">Inactif</span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {adherent.typeRelation === "partenaire_direct" && (
                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800">
                    Direct
                  </Badge>
                )}
                {adherent.typeRelation === "sous_revendeur" && (
                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
                    Sous-rev.
                  </Badge>
                )}
                {!adherent.typeRelation && (
                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-slate-100 text-slate-500 border-dashed border-slate-300 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-600">
                    À classer
                  </Badge>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── RelationSection ────────────────────────────────────────────────────────────

export function RelationSection({
  reseller,
  canEdit,
}: {
  reseller: Reseller;
  canEdit: boolean;
}) {
  const { data: allResellers = [] } = useResellers();
  const { toast } = useToast();
  const parentReseller = (allResellers as any[]).find(
    (r: any) => r.id === (reseller as any).revendeurParentId,
  );
  const [editing, setEditing] = useState(false);
  const [relationType, setRelationType] = useState<string>((reseller as any).typeRelation || "");
  const [parentId, setParentId] = useState<string>((reseller as any).revendeurParentId || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`/api/resellers/${reseller.id}/relation`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type_relation: relationType || null, revendeur_parent_id: parentId || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Erreur");
      queryClient.invalidateQueries({ queryKey: ["/api/resellers"] });
      setEditing(false);
      toast({ title: "Relation mise à jour" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
          Relation partenaire
        </span>
        {canEdit && !editing && (
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
            onClick={() => {
              setRelationType((reseller as any).typeRelation || "");
              setParentId((reseller as any).revendeurParentId || "");
              setEditing(true);
            }}
            data-testid="button-edit-relation"
          >
            <Pencil className="w-3 h-3 mr-1" />Modifier
          </Button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2 pt-1">
          <Select value={relationType || "_none"} onValueChange={(v) => {
            setRelationType(v === "_none" ? "" : v);
            if (v !== "sous_revendeur") setParentId("");
          }}>
            <SelectTrigger className="h-8 text-xs" data-testid="select-type-relation">
              <SelectValue placeholder="— Sélectionner —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">— Non classifié —</SelectItem>
              <SelectItem value="partenaire_direct">Partenaire direct</SelectItem>
              <SelectItem value="sous_revendeur">Sous-revendeur</SelectItem>
            </SelectContent>
          </Select>
          {relationType === "sous_revendeur" && (
            <Select value={parentId || "_none"} onValueChange={(v) => setParentId(v === "_none" ? "" : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid="select-parent-reseller">
                <SelectValue placeholder="— Centrale de rattachement —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— Choisir —</SelectItem>
                {(allResellers as any[])
                  .filter((r: any) => r.id !== reseller.id && (r as any).typeRelation !== "sous_revendeur")
                  .map((r: any) => (
                    <SelectItem key={r.id} value={r.id}>
                      {(r as any).nomCommercial || r.companyName}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
          <div className="flex gap-2 justify-end pt-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(false)}>Annuler</Button>
            <Button size="sm" className="h-7 text-xs" onClick={handleSave} disabled={saving} data-testid="button-save-relation">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Enregistrer"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          {(reseller as any).typeRelation === "partenaire_direct" && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800 text-xs">
              Partenaire direct
            </Badge>
          )}
          {(reseller as any).typeRelation === "sous_revendeur" && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800 text-xs">
              Sous-revendeur
            </Badge>
          )}
          {!(reseller as any).typeRelation && (
            <Badge variant="outline" className="bg-slate-100 text-slate-500 border-dashed border-slate-300 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-600 text-xs">
              À classer
            </Badge>
          )}
          {parentReseller && (
            <span className="text-xs text-muted-foreground">
              Centrale : <strong>{(parentReseller as any).nomCommercial || (parentReseller as any).companyName}</strong>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── TarificationSection ────────────────────────────────────────────────────────

export function TarificationSection({ reseller }: { reseller: Reseller }) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const canWriteNiveau = canDo(profile?.accessRole);
  const [editing, setEditing] = useState(false);
  const [niveauValue, setNiveauValue] = useState<string>((reseller as any).niveauTarifaire || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`/api/resellers/${reseller.id}/niveau-tarifaire`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ niveau_tarifaire: niveauValue || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Erreur");
      queryClient.invalidateQueries({ queryKey: ["/api/resellers"] });
      setEditing(false);
      toast({ title: "Niveau tarifaire mis à jour" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
          Tarification partenaire
        </span>
        {canWriteNiveau && !editing && (
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
            onClick={() => { setNiveauValue((reseller as any).niveauTarifaire || ""); setEditing(true); }}
            data-testid="button-edit-niveau"
          >
            <Pencil className="w-3 h-3 mr-1" />Modifier
          </Button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2 pt-1">
          <Select value={niveauValue || "_none"} onValueChange={(v) => setNiveauValue(v === "_none" ? "" : v)}>
            <SelectTrigger className="h-8 text-xs" data-testid="select-niveau-tarifaire">
              <SelectValue placeholder="— Sélectionner —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">— Non assigné —</SelectItem>
              <SelectItem value="sous_distributeur_premium">Sous-distributeur Premium</SelectItem>
              <SelectItem value="revendeur_niveau_2">Revendeur niveau 2</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2 justify-end pt-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(false)}>Annuler</Button>
            <Button size="sm" className="h-7 text-xs" onClick={handleSave} disabled={saving} data-testid="button-save-niveau">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Enregistrer"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          {(reseller as any).niveauTarifaire === "sous_distributeur_premium" && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800">
              Sous-distributeur Premium
            </Badge>
          )}
          {(reseller as any).niveauTarifaire === "revendeur_niveau_2" && (
            <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-800">
              Revendeur niveau 2
            </Badge>
          )}
          {!(reseller as any).niveauTarifaire && (
            <span className="text-xs text-muted-foreground italic">Non assigné</span>
          )}
          {(reseller as any).niveauTarifaireSetAt && (
            <p className="text-[11px] text-muted-foreground">
              Réglé le {new Date((reseller as any).niveauTarifaireSetAt).toLocaleDateString("fr-FR")}
            </p>
          )}
          {!canWriteNiveau && (
            <p className="text-[11px] text-muted-foreground italic">Édition réservée aux Admin</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SitesSection ─────────────────────────────────────────────────────────────

export function SitesSection({ resellerId, canEdit }: { resellerId: string; canEdit: boolean }) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptySiteForm });
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: sites = [], isLoading, refetch } = useQuery<any[]>({
    queryKey: ["/api/revendeurs", resellerId, "sites"],
    queryFn: async () => {
      const r = await authFetch(`/api/revendeurs/${resellerId}/sites`);
      if (!r.ok) throw new Error("Erreur chargement sites");
      return r.json();
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      if (editingId) {
        const r = await authFetch(`/api/revendeur-sites/${editingId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        if (!r.ok) {
          const e = await r.json();
          throw new Error(e.message);
        }
        return r.json();
      } else {
        const r = await authFetch(`/api/revendeurs/${resellerId}/sites`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        if (!r.ok) {
          const e = await r.json();
          throw new Error(e.message);
        }
        return r.json();
      }
    },
    onSuccess: () => {
      refetch();
      setEditingId(null);
      setForm({ ...emptySiteForm });
      setDialogOpen(false);
    },
    onError: (e: any) =>
      toast({ variant: "destructive", title: "Erreur", description: e.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (siteId: string) => {
      const r = await authFetch(`/api/revendeur-sites/${siteId}`, { method: "DELETE" });
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.message);
      }
    },
    onSuccess: () => {
      refetch();
      setDeleteId(null);
    },
    onError: (e: any) =>
      toast({ variant: "destructive", title: "Erreur", description: e.message }),
  });

  const defaultMutation = useMutation({
    mutationFn: async (siteId: string) => {
      const r = await authFetch(`/api/revendeur-sites/${siteId}/defaut`, { method: "POST" });
      if (!r.ok) throw new Error("Erreur");
    },
    onSuccess: () => refetch(),
    onError: (e: any) =>
      toast({ variant: "destructive", title: "Erreur", description: e.message }),
  });

  function openNew() {
    setEditingId(null);
    setForm({ ...emptySiteForm });
    setDialogOpen(true);
  }

  function openEdit(site: any) {
    setEditingId(site.id);
    setForm({
      nom: site.nom ?? "",
      type_site: site.typeSite ?? "point_de_vente",
      livraison_adresse: site.livraisonAdresse ?? "",
      livraison_code_postal: site.livraisonCodePostal ?? "",
      livraison_ville: site.livraisonVille ?? "",
      livraison_pays: site.livraisonPays ?? "France",
      contact_reception_nom: site.contactReceptionNom ?? "",
      contact_reception_email: site.contactReceptionEmail ?? "",
      contact_reception_tel: site.contactReceptionTel ?? "",
      livraison_horaires: site.livraisonHoraires ?? "",
      livraison_rdv_obligatoire: !!site.livraisonRdvObligatoire,
      livraison_notes: site.livraisonNotes ?? "",
      est_site_par_defaut: !!site.estSiteParDefaut,
      latitude: site.latitude ?? null,
      longitude: site.longitude ?? null,
      geocode_score: site.geocodeScore ?? null,
    });
    setDialogOpen(true);
  }

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <Navigation className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">Sites de livraison ({sites.length})</span>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto h-7 text-xs"
            onClick={openNew}
            data-testid="button-add-site"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Ajouter
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-xs py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Chargement…
        </div>
      ) : sites.length === 0 ? (
        <p className="text-muted-foreground text-xs">Aucun site enregistré</p>
      ) : (
        <div className="space-y-1.5">
          {sites.map((site: any) => (
            <div
              key={site.id}
              className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-muted/30 border border-border/40"
              data-testid={`site-card-${site.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm truncate">{site.nom}</span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] px-1.5 py-0 ${SITE_TYPE_COLORS[site.typeSite] ?? SITE_TYPE_COLORS.autre}`}
                  >
                    {SITE_TYPE_LABELS[site.typeSite] ?? site.typeSite}
                  </Badge>
                  {site.estSiteParDefaut && (
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800"
                    >
                      <Star className="w-2.5 h-2.5 mr-0.5 fill-current" />
                      Défaut
                    </Badge>
                  )}
                  {site.geocodeStatut === "ok" ? (
                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
                      <CheckCircle2 className="w-3 h-3" />
                      géocodé
                    </span>
                  ) : site.geocodeStatut === "echec" ? (
                    <span className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-0.5">
                      <Circle className="w-3 h-3" />
                      non géocodé
                    </span>
                  ) : null}
                </div>
                {(site.livraisonAdresse || site.livraisonVille) && (
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                    <MapPin className="w-3 h-3 shrink-0" />
                    {[site.livraisonAdresse, site.livraisonCodePostal, site.livraisonVille]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                )}
                {site.contactReceptionNom && (
                  <div className="text-xs text-muted-foreground">
                    Contact : {site.contactReceptionNom}
                  </div>
                )}
              </div>
              {canEdit && (
                <div className="flex items-center gap-1 shrink-0">
                  {!site.estSiteParDefaut && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title="Définir comme site par défaut"
                      onClick={() => defaultMutation.mutate(site.id)}
                      disabled={defaultMutation.isPending}
                      data-testid={`button-site-defaut-${site.id}`}
                    >
                      <Star className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => openEdit(site)}
                    data-testid={`button-site-edit-${site.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    onClick={() => setDeleteId(site.id)}
                    data-testid={`button-site-delete-${site.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) {
            setEditingId(null);
            setForm({ ...emptySiteForm });
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Modifier le site" : "Nouveau site de livraison"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs">Nom du site *</Label>
              <Input
                value={form.nom}
                onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
                placeholder="ex: Agence Lyon"
                data-testid="input-site-nom"
              />
            </div>
            <div>
              <Label className="text-xs">Type de site</Label>
              <Select
                value={form.type_site}
                onValueChange={(v) => setForm((f) => ({ ...f, type_site: v }))}
              >
                <SelectTrigger data-testid="select-site-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="point_de_vente">Point de vente</SelectItem>
                  <SelectItem value="depot">Dépôt</SelectItem>
                  <SelectItem value="sav">SAV</SelectItem>
                  <SelectItem value="siege">Siège</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Adresse de livraison</Label>
                <AddressAutocomplete
                  value={form.livraison_adresse}
                  onChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      livraison_adresse: v,
                      latitude: null,
                      longitude: null,
                      geocode_score: null,
                    }))
                  }
                  onSelect={(sel) =>
                    setForm((f) => ({
                      ...f,
                      livraison_adresse: sel.voie,
                      livraison_code_postal: sel.code_postal,
                      livraison_ville: sel.ville,
                      latitude: sel.latitude,
                      longitude: sel.longitude,
                      geocode_score: sel.score,
                    }))
                  }
                  placeholder="Saisir une adresse…"
                  data-testid="input-site-adresse"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Code postal</Label>
                  <Input
                    value={form.livraison_code_postal}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, livraison_code_postal: e.target.value }))
                    }
                    placeholder="Code postal"
                    data-testid="input-site-cp"
                  />
                </div>
                <div>
                  <Label className="text-xs">Ville</Label>
                  <Input
                    value={form.livraison_ville}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, livraison_ville: e.target.value }))
                    }
                    placeholder="Ville"
                    data-testid="input-site-ville"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Contact réception</Label>
                <Input
                  value={form.contact_reception_nom}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, contact_reception_nom: e.target.value }))
                  }
                  placeholder="Nom"
                  data-testid="input-site-contact-nom"
                />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={form.contact_reception_email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, contact_reception_email: e.target.value }))
                  }
                  placeholder="email@…"
                  data-testid="input-site-contact-email"
                />
              </div>
              <div>
                <Label className="text-xs">Tél</Label>
                <Input
                  value={form.contact_reception_tel}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, contact_reception_tel: e.target.value }))
                  }
                  placeholder="06…"
                  data-testid="input-site-contact-tel"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Horaires de livraison</Label>
              <Input
                value={form.livraison_horaires}
                onChange={(e) =>
                  setForm((f) => ({ ...f, livraison_horaires: e.target.value }))
                }
                placeholder="Lun–Ven 9h–17h"
                data-testid="input-site-horaires"
              />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={form.livraison_notes}
                onChange={(e) =>
                  setForm((f) => ({ ...f, livraison_notes: e.target.value }))
                }
                rows={2}
                placeholder="Informations complémentaires…"
                data-testid="textarea-site-notes"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="rdv-obligatoire"
                checked={form.livraison_rdv_obligatoire}
                onChange={(e) =>
                  setForm((f) => ({ ...f, livraison_rdv_obligatoire: e.target.checked }))
                }
                className="rounded"
                data-testid="checkbox-site-rdv"
              />
              <Label htmlFor="rdv-obligatoire" className="text-xs cursor-pointer">
                RDV obligatoire pour livraison
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="site-defaut"
                checked={form.est_site_par_defaut}
                onChange={(e) =>
                  setForm((f) => ({ ...f, est_site_par_defaut: e.target.checked }))
                }
                className="rounded"
                data-testid="checkbox-site-defaut"
              />
              <Label htmlFor="site-defaut" className="text-xs cursor-pointer">
                Site par défaut
              </Label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={() => saveMutation.mutate(form)}
              disabled={!form.nom || saveMutation.isPending}
              data-testid="button-site-save"
            >
              {saveMutation.isPending && (
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
              )}
              {editingId ? "Enregistrer" : "Créer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce site ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le site sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-site-delete-confirm"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── ROBOT_MODELS ─────────────────────────────────────────────────────────────

const ROBOT_MODELS = ["CC1", "CC1 Pro", "BellaBot", "PuduBot2", "FlashBot", "T300", "KettyBot", "MT1", "SH1"];

// ─── PuduRobotsSection ────────────────────────────────────────────────────────

export function PuduRobotsSection({ resellerId }: { resellerId: string }) {
  const { data: robots = [], isLoading, isError } = useQuery<any[]>({
    queryKey: ["/api/pudu/reseller-robots", resellerId],
    queryFn: () => authFetch(`/api/pudu/reseller-robots/${resellerId}`).then(r => r.json()),
  });

  const [lightbox, setLightbox] = useState<{ url: string; label: string } | null>(null);
  const openLightbox = useCallback((url: string, label: string) => setLightbox({ url, label }), []);
  const closeLightbox = useCallback(() => setLightbox(null), []);
  useEffect(() => {
    if (!lightbox) return;
    const fn = (e: KeyboardEvent) => { if (e.key === "Escape") closeLightbox(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [lightbox, closeLightbox]);

  if (isLoading) {
    return (
      <div className="border-t pt-3 mt-3">
        <div className="flex items-center gap-2 mb-2">
          <Bot className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">Robots Pudu Cloud</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="border-t pt-3 mt-3">
        <div className="flex items-center gap-2 mb-2">
          <Bot className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">Robots Pudu Cloud</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-destructive py-2">
          <AlertTriangle className="w-3 h-3" /> Impossible de contacter Pudu Cloud. Vérifiez la configuration de synchronisation.
        </div>
      </div>
    );
  }

  return (
    <div className="border-t pt-3 mt-3" data-testid="section-pudu-robots">
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/30 rounded-full p-2"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-[90vw] max-h-[80vh] flex flex-col items-center gap-4" onClick={e => e.stopPropagation()}>
            <img
              src={lightbox.url}
              alt={lightbox.label}
              className="max-w-full max-h-[70vh] object-contain drop-shadow-2xl rounded-lg"
            />
            <p className="text-white font-medium text-base text-center px-4">{lightbox.label}</p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <Bot className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">Robots Pudu Cloud ({robots.length})</span>
      </div>

      {robots.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Aucun robot synchronisé depuis Pudu Cloud pour ce revendeur.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border/50">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/50 bg-muted/30">
                <th className="text-left px-3 py-2 font-medium text-muted-foreground w-12"></th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Modèle</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">N° Série</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden md:table-cell">MAC</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden lg:table-cell">Magasin</th>
                <th className="text-right px-3 py-2 font-medium text-muted-foreground hidden sm:table-cell">Synchro</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {robots.map((robot: any) => (
                <tr
                  key={robot.id}
                  className="hover:bg-muted/20 transition-colors"
                  data-testid={`pudu-robot-${robot.robotSn}`}
                >
                  <td className="px-3 py-2">
                    <div
                      className={`w-12 h-12 rounded-lg bg-muted overflow-hidden flex items-center justify-center shrink-0 relative group ${robot.productImageUrl ? "cursor-zoom-in" : ""}`}
                      onClick={() => robot.productImageUrl && openLightbox(robot.productImageUrl, robot.productName || robot.robotType || "Robot")}
                    >
                      {robot.productImageUrl ? (
                        <>
                          <img
                            src={robot.productImageUrl}
                            alt={robot.productName || robot.robotType || "Robot"}
                            className="w-full h-full object-contain p-1"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center transition-colors">
                            <ZoomIn className="w-3 h-3 text-white opacity-0 group-hover:opacity-90 transition-opacity" />
                          </div>
                        </>
                      ) : (
                        <Bot className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <p className="font-medium leading-tight truncate max-w-[160px]">
                      {robot.productName || robot.robotType || "Robot Pudu"}
                    </p>
                    {robot.productCode && (
                      <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">
                        {robot.productCode}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <p className="font-mono text-[11px] text-foreground/80">{robot.robotSn}</p>
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell">
                    <p className="font-mono text-[11px] text-muted-foreground">{robot.robotMac || "—"}</p>
                  </td>
                  <td className="px-3 py-2 hidden lg:table-cell">
                    <p className="truncate max-w-[160px] text-muted-foreground">{robot.shopName || "—"}</p>
                  </td>
                  <td className="px-3 py-2 text-right hidden sm:table-cell">
                    <p className="text-[10px] text-muted-foreground">
                      {robot.lastSyncAt
                        ? format(new Date(robot.lastSyncAt), "dd/MM/yy HH:mm", { locale: fr })
                        : "—"}
                    </p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── RobotsSection ────────────────────────────────────────────────────────────

export function RobotsSection({ resellerId, canEdit }: { resellerId: string; canEdit: boolean }) {
  const { data: robots = [] } = useRobotsReseller(resellerId);
  const createRobot = useCreateRobotReseller();
  const updateRobot = useUpdateRobotReseller();
  const deleteRobot = useDeleteRobotReseller();
  const [showForm, setShowForm] = useState(false);
  const [editingRobot, setEditingRobot] = useState<RobotClient | null>(null);
  const [robotForm, setRobotForm] = useState({
    modele: "", numeroSerie: "", macAddress: "", dateAchat: "", dateInstallation: "", garantieFin: "", statut: "actif", notes: ""
  });
  const { toast } = useToast();

  const resetForm = () => {
    setRobotForm({ modele: "", numeroSerie: "", macAddress: "", dateAchat: "", dateInstallation: "", garantieFin: "", statut: "actif", notes: "" });
    setShowForm(false);
    setEditingRobot(null);
  };

  const handleSubmitRobot = async () => {
    if (!robotForm.modele || !robotForm.numeroSerie) return;
    try {
      if (editingRobot) {
        await updateRobot.mutateAsync({
          id: editingRobot.id,
          resellerId,
          modele: robotForm.modele,
          numeroSerie: robotForm.numeroSerie,
          macAddress: robotForm.macAddress || null,
          dateAchat: robotForm.dateAchat || null,
          dateInstallation: robotForm.dateInstallation || null,
          garantieFin: robotForm.garantieFin || null,
          statut: robotForm.statut as any,
          notes: robotForm.notes || null,
        });
        toast({ title: "Robot modifié" });
      } else {
        await createRobot.mutateAsync({
          resellerId,
          modele: robotForm.modele,
          numeroSerie: robotForm.numeroSerie,
          macAddress: robotForm.macAddress || null,
          dateAchat: robotForm.dateAchat || null,
          dateInstallation: robotForm.dateInstallation || null,
          garantieFin: robotForm.garantieFin || null,
          statut: robotForm.statut as any,
          notes: robotForm.notes || null,
        });
        toast({ title: "Robot ajouté" });
      }
      resetForm();
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  const startEdit = (r: RobotClient) => {
    setEditingRobot(r);
    setRobotForm({
      modele: r.modele,
      numeroSerie: r.numeroSerie,
      macAddress: r.macAddress || "",
      dateAchat: r.dateAchat || "",
      dateInstallation: r.dateInstallation || "",
      garantieFin: r.garantieFin || "",
      statut: r.statut || "actif",
      notes: r.notes || "",
    });
    setShowForm(true);
  };

  const handleDeleteRobot = async (r: RobotClient) => {
    try {
      await deleteRobot.mutateAsync({ id: r.id, resellerId });
      toast({ title: "Robot supprimé" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  const robotStatusLabels: Record<string, string> = {
    actif: "Actif",
    en_reparation: "En réparation",
    hors_service: "Hors service",
  };

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Robots ({robots.length})</span>
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => { resetForm(); setShowForm(true); }} data-testid="button-add-robot">
            <Plus className="w-3 h-3 mr-1" /> Ajouter un robot
          </Button>
        )}
      </div>

      {showForm && (
        <div className="space-y-2 p-3 rounded-lg bg-muted/30 border mb-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Modèle *</Label>
              <Select value={robotForm.modele} onValueChange={v => setRobotForm({ ...robotForm, modele: v })}>
                <SelectTrigger data-testid="select-robot-model"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {ROBOT_MODELS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>N° de série *</Label>
              <Input value={robotForm.numeroSerie} onChange={e => setRobotForm({ ...robotForm, numeroSerie: e.target.value })} data-testid="input-robot-serial" />
            </div>
            <div className="space-y-1">
              <Label>Adresse MAC</Label>
              <Input value={robotForm.macAddress} onChange={e => setRobotForm({ ...robotForm, macAddress: e.target.value })} data-testid="input-robot-mac" />
            </div>
            <div className="space-y-1">
              <Label>Date d'achat</Label>
              <Input type="date" value={robotForm.dateAchat} onChange={e => setRobotForm({ ...robotForm, dateAchat: e.target.value })} data-testid="input-robot-date-achat" />
            </div>
            <div className="space-y-1">
              <Label>Date d'installation</Label>
              <Input type="date" value={robotForm.dateInstallation} onChange={e => setRobotForm({ ...robotForm, dateInstallation: e.target.value })} data-testid="input-robot-date-install" />
            </div>
            <div className="space-y-1">
              <Label>Fin de garantie</Label>
              <Input type="date" value={robotForm.garantieFin} onChange={e => setRobotForm({ ...robotForm, garantieFin: e.target.value })} data-testid="input-robot-garantie" />
            </div>
            <div className="space-y-1">
              <Label>Statut</Label>
              <Select value={robotForm.statut} onValueChange={v => setRobotForm({ ...robotForm, statut: v })}>
                <SelectTrigger data-testid="select-robot-statut"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="actif">Actif</SelectItem>
                  <SelectItem value="en_reparation">En réparation</SelectItem>
                  <SelectItem value="hors_service">Hors service</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea value={robotForm.notes} onChange={e => setRobotForm({ ...robotForm, notes: e.target.value })} rows={2} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={resetForm}>Annuler</Button>
            <Button size="sm" onClick={handleSubmitRobot} disabled={createRobot.isPending || updateRobot.isPending || !robotForm.modele || !robotForm.numeroSerie} data-testid="button-submit-robot">
              {(createRobot.isPending || updateRobot.isPending) ? "Enregistrement..." : editingRobot ? "Modifier" : "Ajouter"}
            </Button>
          </div>
        </div>
      )}

      {robots.length === 0 ? (
        <p className="text-muted-foreground text-xs">Aucun robot associé</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/20">
              <TableHead>Photo</TableHead>
              <TableHead>Modèle</TableHead>
              <TableHead>N° série</TableHead>
              <TableHead>Date achat</TableHead>
              <TableHead>Statut</TableHead>
              {canEdit && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {robots.map((r: RobotClient) => (
              <TableRow key={r.id} data-testid={`robot-row-${r.id}`}>
                <TableCell>
                  {r.profilePhotoUrl ? (
                    <img src={r.profilePhotoUrl} alt="" className="w-8 h-8 rounded object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                      <Bot className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium">{r.modele}</TableCell>
                <TableCell className="font-mono text-xs">{r.numeroSerie}</TableCell>
                <TableCell className="text-xs">{r.dateAchat ? format(new Date(r.dateAchat + "T00:00:00"), "dd/MM/yyyy", { locale: fr }) : '—'}</TableCell>
                <TableCell>
                  <StatusBadge status={r.statut} type="robotClient" label={robotStatusLabels[r.statut] || r.statut} />
                </TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => startEdit(r)} data-testid={`button-edit-robot-${r.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteRobot(r)} disabled={deleteRobot.isPending} data-testid={`button-delete-robot-${r.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ─── ResellerTicketsSection ───────────────────────────────────────────────────

const ticketStatusLabels: Record<string, string> = {
  nouveau: "Nouveau",
  en_cours: "En cours",
  en_attente_piece: "En attente pièce",
  resolu: "Résolu",
  ferme: "Fermé",
};

export function ResellerTicketsSection({ resellerId }: { resellerId: string }) {
  const [, navigate] = useLocation();
  const { data: resellerTickets = [] } = useTicketsByReseller(resellerId);
  const tickets = resellerTickets as any[];

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <TicketCheck className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">Tickets ({tickets.length})</span>
      </div>
      {tickets.length === 0 ? (
        <p className="text-muted-foreground text-xs">Aucun ticket associé</p>
      ) : (
        <div className="space-y-1">
          {tickets.map((t) => (
            <div
              key={t.id}
              className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/30 cursor-pointer hover:bg-muted/60 transition-colors gap-2"
              onClick={() => navigate(`/tickets?open=${t.ticketNumber}`)}
              data-testid={`reseller-ticket-${t.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-xs shrink-0">{t.ticketNumber}</span>
                {t.problemDescription && (
                  <span className="text-xs text-muted-foreground truncate">
                    {t.problemDescription.trim().slice(0, 60)}{t.problemDescription.trim().length > 60 ? "…" : ""}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge status={t.status} type="ticket" label={ticketStatusLabels[t.status]} />
                <span className="text-xs text-muted-foreground">
                  {t.receivedAt ? format(new Date(t.receivedAt), "dd/MM/yyyy", { locale: fr }) : ""}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── CommandesResellerSection ─────────────────────────────────────────────────

export function CommandesResellerSection({ resellerId }: { resellerId: string }) {
  const [, navigate] = useLocation();
  const { data: commandes = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/resellers", resellerId, "commandes"],
    queryFn: async () => {
      const res = await authFetch(`/api/resellers/${resellerId}/commandes`);
      if (!res.ok) throw new Error("Failed to fetch commandes");
      return res.json();
    },
    enabled: !!resellerId,
  });

  if (isLoading || commandes.length === 0) return null;

  const totalHt = commandes.reduce((s, c) => s + (parseFloat(c.montantHt) || 0), 0);

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <ShoppingCart className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">Commandes ({commandes.length})</span>
      </div>
      <div className="space-y-1">
        {commandes.slice(0, 8).map((c) => (
          <div
            key={c.id}
            className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/30 text-xs cursor-pointer hover:bg-muted/60 transition-colors"
            onClick={() => c.id && navigate(`/commandes/${c.id}`)}
            data-testid={`cmd-row-${c.id}`}
          >
            <div className="flex items-center gap-2">
              <span className="font-mono">{c.zohoNumero || "—"}</span>
              {c.dateCommande && (
                <span className="text-muted-foreground">
                  {format(new Date(c.dateCommande + "T00:00:00"), "dd/MM/yyyy", { locale: fr })}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {c.statut && <Badge variant="outline" className="text-[10px]">{c.statut}</Badge>}
              <span className="font-medium">
                {c.montantHt ? `${parseFloat(c.montantHt).toLocaleString("fr-FR")} € HT` : "—"}
              </span>
            </div>
          </div>
        ))}
        {commandes.length > 8 && (
          <p className="text-xs text-muted-foreground text-center pt-1">+ {commandes.length - 8} autres commandes</p>
        )}
      </div>
      <div className="mt-2 pt-2 border-t flex items-center justify-between text-xs font-semibold text-muted-foreground">
        <span>Total partenariat</span>
        <span className="text-foreground">{totalHt.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} € HT</span>
      </div>
    </div>
  );
}

// ─── ExtensionsGarantieSection ────────────────────────────────────────────────

export function ExtensionsGarantieSection() {
  const { data: extensions = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/extensions-garantie"],
    queryFn: () => authFetch("/api/extensions-garantie").then(r => r.json()),
  });

  const fmtE = (v: string | number | null) => v != null ? `${parseFloat(String(v)).toFixed(2)} €` : "—";

  const grouped = extensions.reduce((acc: Record<string, any[]>, ext: any) => {
    const key = ext.robotNom || "Produit inconnu";
    if (!acc[key]) acc[key] = [];
    acc[key].push(ext);
    return acc;
  }, {});

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="h-4 w-4 text-violet-500" />
        <span className="font-medium">Extensions de garantie — catalogue global ({extensions.length})</span>
      </div>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Chargement...</p>
      ) : extensions.length === 0 ? (
        <p className="text-xs text-muted-foreground">Aucune extension de garantie configurée</p>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped).map(([productName, exts]) => (
            <div key={productName}>
              <p className="text-xs font-medium text-foreground/70 mb-1">{productName}</p>
              <div className="overflow-hidden rounded-lg border border-border text-xs">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/40">
                      <th className="text-left p-2 font-medium text-muted-foreground">Extension</th>
                      <th className="text-right p-2 font-medium text-muted-foreground">Revendeur</th>
                      <th className="text-right p-2 font-medium text-muted-foreground">Client final</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {exts.map((ext: any) => (
                      <tr key={ext.id}>
                        <td className="p-2 text-foreground/80">{ext.dureeAns} an{ext.dureeAns > 1 ? "s" : ""} <span className="text-muted-foreground font-mono">{ext.codeArticle}</span></td>
                        <td className="p-2 text-right font-medium">{fmtE(ext.prixRevendeur)}</td>
                        <td className="p-2 text-right font-medium">{fmtE(ext.prixClientFinal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PerformanceSection ──────────────────────────────────────────────────────

export function PerformanceSection({ resellerId }: { resellerId: string }) {
  const { data: contrats = [] } = useRevendeurContrats(resellerId);
  const withPerf = (contrats as any[]).filter(c => (c.objectifAnnuel ?? 0) > 0 || (c.totalRobotsVendus ?? 0) > 0);
  if (withPerf.length === 0) return null;
  const sorted = [...withPerf].sort((a, b) => a.annee - b.annee);

  const getColors = (taux: number) => {
    if (taux > 100) return { bar: "bg-violet-500", text: "text-violet-700 dark:text-violet-400", bg: "bg-violet-50 dark:bg-violet-900/10 border-violet-200 dark:border-violet-800" };
    if (taux >= 80) return { bar: "bg-green-500", text: "text-green-700 dark:text-green-400", bg: "bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800" };
    if (taux >= 50) return { bar: "bg-orange-400", text: "text-orange-700 dark:text-orange-400", bg: "bg-orange-50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-800" };
    return { bar: "bg-red-400", text: "text-red-700 dark:text-red-400", bg: "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800" };
  };

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center gap-2 mb-3">
        <Target className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">Performance commerciale</span>
      </div>
      <div className="space-y-2">
        {sorted.map((c: any) => {
          const taux = parseFloat(c.tauxAtteinte ?? "0") || 0;
          const col = getColors(taux);
          const pct = Math.min(taux, 100);
          return (
            <div key={c.id} className={`rounded-lg p-3 border ${col.bg}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">Année {c.annee}</span>
                  {c.statut && (
                    <Badge variant="outline" className="text-[10px]">{c.statut}</Badge>
                  )}
                </div>
                <span className={`font-bold text-sm ${col.text}`}>{taux.toFixed(1)} %</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 mb-2">
                <div className={`h-2 rounded-full transition-all ${col.bar}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{c.totalRobotsVendus ?? 0} robots vendus</span>
                <span>Objectif : {c.objectifAnnuel ?? 0}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ContratsSection ─────────────────────────────────────────────────────────

export function ContratsSection({ resellerId, canEdit }: { resellerId: string; canEdit: boolean }) {
  const { data: contrats = [] } = useRevendeurContrats(resellerId);
  const createContrat = useCreateRevendeurContrat();
  const updateContrat = useUpdateRevendeurContrat();
  const deleteContrat = useDeleteRevendeurContrat();
  const [showForm, setShowForm] = useState(false);
  const [editingContrat, setEditingContrat] = useState<RevendeurContrat | null>(null);
  const { toast } = useToast();

  const maxAnnee = contrats.length > 0 ? Math.max(...contrats.map(c => c.annee)) : 0;

  const [contratForm, setContratForm] = useState({
    annee: (maxAnnee + 1).toString(),
    label: "",
    dateDebut: "",
    dateFin: "",
    statut: "",
    notes: "",
  });

  const resetForm = () => {
    const newMax = contrats.length > 0 ? Math.max(...contrats.map(c => c.annee)) : 0;
    setContratForm({ annee: (newMax + 1).toString(), label: "", dateDebut: "", dateFin: "", statut: "", notes: "" });
    setShowForm(false);
    setEditingContrat(null);
  };

  const handleSubmitContrat = async () => {
    try {
      if (editingContrat) {
        await updateContrat.mutateAsync({
          id: editingContrat.id,
          revendeurId: resellerId,
          annee: parseInt(contratForm.annee),
          label: contratForm.label || undefined,
          dateDebut: contratForm.dateDebut || null,
          dateFin: contratForm.dateFin || null,
          statut: contratForm.statut || null,
          notes: contratForm.notes || undefined,
        } as any);
        toast({ title: "Contrat modifié" });
      } else {
        await createContrat.mutateAsync({
          revendeurId: resellerId,
          annee: parseInt(contratForm.annee),
          label: contratForm.label || undefined,
          dateDebut: contratForm.dateDebut || null,
          dateFin: contratForm.dateFin || null,
          statut: contratForm.statut || null,
          notes: contratForm.notes || undefined,
        } as any);
        toast({ title: "Contrat ajouté" });
      }
      resetForm();
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  const startEditContrat = (c: RevendeurContrat) => {
    setEditingContrat(c);
    setContratForm({
      annee: c.annee.toString(),
      label: c.label || "",
      dateDebut: c.dateDebut || "",
      dateFin: c.dateFin || "",
      statut: (c as any).statut || "",
      notes: c.notes || "",
    });
    setShowForm(true);
  };

  const handleDeleteContrat = async (c: RevendeurContrat) => {
    try {
      await deleteContrat.mutateAsync({ id: c.id, revendeurId: resellerId });
      toast({ title: "Contrat supprimé" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  const sorted = [...contrats].sort((a, b) => b.annee - a.annee);

  const getStatutBadge = (statut: string | null | undefined) => {
    if (!statut) return null;
    if (statut === "Revendeur Premium") return <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800 text-[10px]">Premium</Badge>;
    if (statut === "Sous Distributeur") return <Badge variant="outline" className="bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800 text-[10px]">Sous-Distrib.</Badge>;
    if (statut === "Partenaire Commercial") return <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800 text-[10px]">Partenaire</Badge>;
    if (statut === "Revendeur") return <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800 text-[10px]">Revendeur</Badge>;
    if (statut === "en_cours") return <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800 text-[10px]">En cours</Badge>;
    if (statut === "expire") return <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800 text-[10px]">Expiré</Badge>;
    return <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700 text-[10px]">{statut}</Badge>;
  };

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Contrats de partenariat ({contrats.length})</span>
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => { resetForm(); setShowForm(true); }} data-testid="button-add-contrat">
            <Plus className="w-3 h-3 mr-1" /> Ajouter une période
          </Button>
        )}
      </div>

      {showForm && (
        <div className="space-y-2 p-3 rounded-lg bg-muted/30 border mb-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Année</Label>
              <Input type="number" value={contratForm.annee} onChange={e => setContratForm({ ...contratForm, annee: e.target.value })} data-testid="input-contrat-annee" />
            </div>
            <div className="space-y-1">
              <Label>Label</Label>
              <Input value={contratForm.label} onChange={e => setContratForm({ ...contratForm, label: e.target.value })} data-testid="input-contrat-label" />
            </div>
            <div className="space-y-1">
              <Label>Date début *</Label>
              <Input type="date" value={contratForm.dateDebut} onChange={e => setContratForm({ ...contratForm, dateDebut: e.target.value })} data-testid="input-contrat-debut" />
            </div>
            <div className="space-y-1">
              <Label>Date fin *</Label>
              <Input type="date" value={contratForm.dateFin} onChange={e => setContratForm({ ...contratForm, dateFin: e.target.value })} data-testid="input-contrat-fin" />
            </div>
            <div className="space-y-1">
              <Label>Statut</Label>
              <Select value={contratForm.statut} onValueChange={v => setContratForm({ ...contratForm, statut: v })}>
                <SelectTrigger data-testid="select-contrat-statut"><SelectValue placeholder="Sans statut" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Sans statut</SelectItem>
                  <SelectItem value="Revendeur">Revendeur</SelectItem>
                  <SelectItem value="Revendeur Premium">Revendeur Premium</SelectItem>
                  <SelectItem value="Sous Distributeur">Sous Distributeur</SelectItem>
                  <SelectItem value="Partenaire Commercial">Partenaire Commercial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea value={contratForm.notes} onChange={e => setContratForm({ ...contratForm, notes: e.target.value })} rows={2} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={resetForm}>Annuler</Button>
            <Button size="sm" onClick={handleSubmitContrat} disabled={createContrat.isPending || updateContrat.isPending || !contratForm.annee} data-testid="button-submit-contrat">
              {(createContrat.isPending || updateContrat.isPending) ? "Enregistrement..." : editingContrat ? "Modifier" : "Ajouter"}
            </Button>
          </div>
        </div>
      )}

      {sorted.length === 0 ? (
        <p className="text-muted-foreground text-xs">Aucun contrat de partenariat</p>
      ) : (
        <div className="space-y-1">
          {sorted.map((c, idx) => {
            const isCurrent = idx === 0;
            const statutVal = (c as any).statut as string | null;
            return (
              <div
                key={c.id}
                className={`flex items-center justify-between py-2 px-3 rounded ${isCurrent ? 'bg-blue-50 dark:bg-blue-900/10 border-l-2 border-l-blue-400' : 'bg-muted/30'}`}
                data-testid={`contrat-row-${c.id}`}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`font-semibold text-sm ${isCurrent ? 'text-blue-700 dark:text-blue-400' : ''}`}>Année {c.annee}</span>
                  {c.label && <span className="text-xs text-muted-foreground">{c.label}</span>}
                  {(c.dateDebut || c.dateFin) && (
                    <span className="text-xs text-muted-foreground">
                      {c.dateDebut ? format(new Date(c.dateDebut + "T00:00:00"), "dd/MM/yyyy", { locale: fr }) : "?"} → {c.dateFin ? format(new Date(c.dateFin + "T00:00:00"), "dd/MM/yyyy", { locale: fr }) : "?"}
                    </span>
                  )}
                  {getStatutBadge(statutVal)}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => startEditContrat(c)} data-testid={`button-edit-contrat-${c.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteContrat(c)} disabled={deleteContrat.isPending} data-testid={`button-delete-contrat-${c.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── PortailAccessSection ────────────────────────────────────────────────────

interface PortailUser {
  id: string;
  resellerId: string;
  email: string;
  role: string;
  prenom: string | null;
  nom: string | null;
  telephone: string | null;
  roleSociete: string | null;
  actif: boolean;
  createdAt: string;
}

export function PortailAccessSection({ resellerId, canEdit }: { resellerId: string; canEdit: boolean }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePrenom, setInvitePrenom] = useState("");
  const [inviteNom, setInviteNom] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "contact">("contact");
  const [inviteRoleSociete, setInviteRoleSociete] = useState("");
  const [inviting, setInviting] = useState(false);

  const { data: users = [], refetch, isLoading } = useQuery<PortailUser[]>({
    queryKey: ['/api/portail/reseller', resellerId, 'users'],
    queryFn: () => authFetch(`/api/portail/reseller/${resellerId}/users`).then(r => r.json()),
  });

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await authFetch(`/api/portail/reseller/${resellerId}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: inviteEmail,
          prenom: invitePrenom,
          nom: inviteNom,
          role: inviteRole,
          roleSociete: inviteRoleSociete,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        toast({ title: "Erreur", description: data.message, variant: "destructive" });
      } else {
        toast({ title: "Invitation envoyée", description: `Un email d'invitation a été envoyé à ${inviteEmail}` });
        setInviteEmail(""); setInvitePrenom(""); setInviteNom(""); setInviteRoleSociete("");
        setOpen(false);
        refetch();
      }
    } catch {
      toast({ title: "Erreur réseau", variant: "destructive" });
    } finally {
      setInviting(false);
    }
  };

  const handleToggle = async (userId: string) => {
    try {
      await authFetch(`/api/portail/users/${userId}/toggle`, { method: 'PUT' });
      refetch();
    } catch {
      toast({ title: "Erreur", variant: "destructive" });
    }
  };

  const handleDelete = async (userId: string, email: string) => {
    if (!confirm(`Supprimer le compte portail de ${email} ?`)) return;
    try {
      await authFetch(`/api/portail/users/${userId}`, { method: 'DELETE' });
      toast({ title: "Compte supprimé" });
      refetch();
    } catch {
      toast({ title: "Erreur", variant: "destructive" });
    }
  };

  return (
    <div className="border-t pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-violet-500" />
          <span className="font-medium">Accès portail partenaire ({users.length})</span>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs" data-testid="button-invite-portail">
                <Send className="w-3.5 h-3.5" />
                Inviter un contact
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Inviter au portail partenaire</DialogTitle>
                <DialogDescription>Un email d'invitation sera envoyé à cette adresse.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleInvite} className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Prénom</Label>
                    <Input value={invitePrenom} onChange={e => setInvitePrenom(e.target.value)} placeholder="Jean" className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nom</Label>
                    <Input value={inviteNom} onChange={e => setInviteNom(e.target.value)} placeholder="Dupont" className="h-8 text-sm" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email <span className="text-destructive">*</span></Label>
                  <Input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="contact@partenaire.fr" required className="h-8 text-sm" data-testid="input-invite-email" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Rôle portail</Label>
                  <Select value={inviteRole} onValueChange={v => setInviteRole(v as any)}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contact">Contact</SelectItem>
                      <SelectItem value="admin">Administrateur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Fonction dans la société</Label>
                  <Input value={inviteRoleSociete} onChange={e => setInviteRoleSociete(e.target.value)} placeholder="Directeur commercial" className="h-8 text-sm" />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button type="submit" size="sm" disabled={inviting} data-testid="button-confirm-invite">
                    {inviting ? "Envoi..." : "Envoyer l'invitation"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Chargement...</p>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 text-center gap-2 bg-muted/20 rounded-xl border border-dashed border-border/60">
          <Users className="w-7 h-7 text-muted-foreground/40" />
          <p className="text-xs text-muted-foreground">Aucun accès portail configuré pour ce revendeur.</p>
          {canEdit && (
            <p className="text-xs text-muted-foreground">Cliquez sur "Inviter un contact" pour donner accès à portail.jobotto.fr</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {users.map(u => (
            <div key={u.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30 border border-border/40 gap-3" data-testid={`portail-user-${u.id}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${u.actif ? "bg-violet-500" : "bg-muted-foreground/40"}`}>
                  {(u.prenom?.[0] || u.email[0]).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate text-foreground">
                    {u.prenom || u.nom ? `${u.prenom || ''} ${u.nom || ''}`.trim() : u.email}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className={`text-xs px-1.5 py-0 ${u.role === "admin" ? "border-violet-400 text-violet-600" : "border-slate-300 text-slate-500"}`}>
                  {u.role === "admin" ? "Admin" : "Contact"}
                </Badge>
                {!u.actif && <Badge variant="outline" className="text-xs px-1.5 py-0 border-red-300 text-red-500">Désactivé</Badge>}
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost" size="icon"
                      className="w-7 h-7"
                      title={u.actif ? "Désactiver l'accès" : "Réactiver l'accès"}
                      onClick={() => handleToggle(u.id)}
                      data-testid={`button-toggle-portail-${u.id}`}
                    >
                      {u.actif ? <UserCheck className="w-3.5 h-3.5 text-green-500" /> : <UserX className="w-3.5 h-3.5 text-muted-foreground" />}
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="w-7 h-7"
                      title="Supprimer"
                      onClick={() => handleDelete(u.id, u.email)}
                      data-testid={`button-delete-portail-${u.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
